/**
 * PreAuthConfirmPage — Buyer authorises a security hold via Stripe.js.
 *
 * Route: /preauth/:preauth_id/confirm?match=<match_id>
 *
 * Flow:
 *  1. Load preauth details (GET /preauth/:id) for client_secret + amount
 *  2. Load buyer's saved cards (GET /payment-methods)
 *  3. If saved cards exist, show picker UI defaulting to default card.
 *     "Use a different card" reveals PaymentElement form.
 *  4a. Saved card path → stripe.confirmCardPayment(secret, { payment_method: pmId })
 *  4b. New card path → stripe.confirmPayment({ elements, redirect: 'if_required' })
 *      Card is always saved (setup_future_usage=off_session is set on the PI
 *      server-side) so the seller can charge a remainder later if needed.
 *  5. On success (requires_capture) → navigate back to /messages/:matchId
 */
import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';
import { Shield, ArrowLeft, Loader2, AlertCircle, ShieldCheck, CreditCard, Check } from 'lucide-react';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// Stable across renders — Stripe warns if options.appearance identity changes
const STRIPE_APPEARANCE = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#1E3A5F',
    fontFamily: 'Manrope, system-ui, sans-serif',
    borderRadius: '8px',
  },
};

const BRAND_LABEL = {
  visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex',
  discover: 'Discover', diners: 'Diners', jcb: 'JCB', unionpay: 'UnionPay',
};
function brandLabel(b) { return BRAND_LABEL[b?.toLowerCase()] || (b || 'Card'); }

// ─────────────────────────────────────────────────────────────────────────────
// Saved-card path — confirm using a stored PaymentMethod ID
// ─────────────────────────────────────────────────────────────────────────────
function SavedCardForm({ preauth, savedCards, onSuccess, onUseDifferent }) {
  const [selectedId, setSelectedId] = useState(
    savedCards.find(c => c.is_default)?.id || savedCards[0]?.id
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const amountDisplay = `$${(preauth.amount_cents / 100).toFixed(2)} AUD`;

  const handlePay = async () => {
    if (!selectedId) return;
    setLoading(true);
    setError('');

    const stripe = await stripePromise;
    const { error: payError, paymentIntent } = await stripe.confirmCardPayment(
      preauth.client_secret,
      { payment_method: selectedId }
    );

    if (payError) {
      setError(payError.message || 'Card authorisation failed. Please try again.');
      setLoading(false);
      return;
    }

    if (paymentIntent?.status === 'requires_capture') {
      onSuccess();
    } else {
      setError(`Unexpected payment status: ${paymentIntent?.status}.`);
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/40 p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-900 dark:text-amber-200 text-sm">
              {amountDisplay} security hold
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 leading-relaxed">
              Your card will be held. After the meetup, the seller may charge
              up to your agreed price — they cannot charge more than that
              without your confirmation.
            </p>
          </div>
        </div>
      </div>

      {/* Card picker */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[var(--theme-text-primary)]">
          Pay with saved card
        </label>
        {savedCards.map(card => {
          const selected = card.id === selectedId;
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => setSelectedId(card.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left
                ${selected
                  ? 'border-[#1E3A5F] bg-[#1E3A5F]/5'
                  : 'border-[var(--theme-border)] bg-[var(--theme-card)] hover:bg-[var(--theme-hover)]'}`}
            >
              <div className="w-10 h-10 rounded-lg bg-[var(--theme-hover)] flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-5 h-5 text-[var(--theme-text-secondary)]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[var(--theme-text-primary)]">
                  {brandLabel(card.brand)} •••• {card.last4}
                  {card.is_default && (
                    <span className="ml-2 text-[10px] font-semibold uppercase text-green-700 dark:text-green-400">
                      Default
                    </span>
                  )}
                </p>
                <p className="text-xs text-[var(--theme-text-muted)]">
                  Expires {String(card.exp_month).padStart(2, '0')}/{String(card.exp_year).slice(-2)}
                </p>
              </div>
              {selected && <Check className="w-5 h-5 text-[#1E3A5F]" />}
            </button>
          );
        })}

        <button
          type="button"
          onClick={onUseDifferent}
          className="text-xs text-[#1E3A5F] hover:underline mt-1"
        >
          Use a different card
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-700/40">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <Button
        type="button"
        onClick={handlePay}
        disabled={loading || !selectedId}
        className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white h-11"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
        {loading ? 'Authorising…' : `Authorise ${amountDisplay} hold`}
      </Button>

      <p className="text-xs text-center text-[var(--theme-text-muted)]">
        Your card will <strong>not</strong> be charged now. Stripe securely processes this hold.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// New-card path — PaymentElement with optional "save card" checkbox
// ─────────────────────────────────────────────────────────────────────────────
function NewCardForm({ preauth, hasSavedCards, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [cardError, setCardError] = useState('');

  const amountDisplay = `$${(preauth.amount_cents / 100).toFixed(2)} AUD`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setCardError('');

    // SFU is set server-side on the PI (always 'off_session' for preauths) so
    // the saved card can be used for a remainder top-up later. No need to set
    // it again here.
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (error) {
      setCardError(error.message || 'Card authorisation failed. Please try again.');
      setLoading(false);
      return;
    }

    if (paymentIntent?.status === 'requires_capture') {
      onSuccess();
    } else {
      setCardError(`Unexpected payment status: ${paymentIntent?.status}. Please contact support.`);
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Summary */}
      <div className="rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/40 p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-900 dark:text-amber-200 text-sm">
              {amountDisplay} security hold
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 leading-relaxed">
              Your card will be held. After the meetup, the seller may charge
              up to your agreed price — they cannot charge more than that
              without your confirmation.
            </p>
          </div>
        </div>
      </div>

      {/* Card input */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-[var(--theme-text-primary)]">
            Card details
          </label>
          {hasSavedCards && (
            <button
              type="button"
              onClick={onCancel}
              className="text-xs text-[#1E3A5F] hover:underline"
            >
              ← Use a saved card
            </button>
          )}
        </div>
        <div className="p-3.5 rounded-xl border border-[var(--theme-border)] bg-[var(--theme-card)]">
          <PaymentElement options={{ wallets: { link: 'never' } }} />
        </div>
      </div>

      {cardError && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-700/40">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{cardError}</p>
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || loading}
        className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white h-11"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
        {loading ? 'Authorising…' : `Authorise ${amountDisplay} hold`}
      </Button>

      <p className="text-xs text-center text-[var(--theme-text-muted)]">
        Your card will <strong>not</strong> be charged now. Stripe securely processes this hold.
      </p>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page component
// ─────────────────────────────────────────────────────────────────────────────
export default function PreAuthConfirmPage() {
  const { preauth_id } = useParams();
  const [searchParams] = useSearchParams();
  const matchId = searchParams.get('match');
  const navigate = useNavigate();
  const { api } = useAuth();

  const [preauth, setPreauth] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [forceNewCard, setForceNewCard] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Load preauth + saved cards in parallel
        const [paRes, cardsRes] = await Promise.all([
          api.get(`/preauth/${preauth_id}`),
          api.get('/payment-methods').catch(() => ({ data: [] })),
        ]);
        const pa = paRes.data;

        if (pa.status !== 'requested') {
          if (pa.status === 'held') {
            setConfirmed(true);
          } else {
            setError(`This hold is no longer active (status: ${pa.status}).`);
          }
          setLoading(false);
          return;
        }

        if (!pa.client_secret) {
          setError('Unable to load hold details. Please contact support.');
          setLoading(false);
          return;
        }

        setPreauth(pa);
        setSavedCards(cardsRes.data || []);
      } catch (err) {
        const detail = err.response?.data?.detail;
        setError(detail || 'Failed to load hold details.');
      } finally {
        setLoading(false);
      }
    })();
  }, [preauth_id, api]);

  const handleSuccess = () => {
    setConfirmed(true);
    toast.success('Hold authorised — no charge until the seller captures it');
    setTimeout(() => {
      navigate(matchId ? `/messages/${matchId}` : '/messages', { replace: true });
    }, 2000);
  };

  const backHref = matchId ? `/messages/${matchId}` : '/messages';
  const showSavedCards = savedCards.length > 0 && !forceNewCard;

  // Elements options for the new-card path — clientSecret + Link disabled via wallets
  const elementsOptions = preauth && !showSavedCards
    ? { clientSecret: preauth.client_secret, appearance: STRIPE_APPEARANCE }
    : null;

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-8">
        <Link
          to={backHref}
          className="inline-flex items-center gap-1.5 text-sm text-[var(--theme-text-muted)] hover:text-[var(--theme-text-primary)] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to chat
        </Link>

        <div className="bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[var(--theme-text-primary)]">
                Authorise Security Hold
              </h1>
              <p className="text-xs text-[var(--theme-text-muted)]">
                Card pre-authorisation — not a charge
              </p>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--theme-text-muted)]" />
            </div>
          )}

          {!loading && error && (
            <div className="flex items-start gap-2 p-4 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-700/40">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-700 dark:text-red-400">{error}</p>
                <Link
                  to={backHref}
                  className="text-xs text-red-600 dark:text-red-500 underline mt-1 inline-block"
                >
                  Return to chat
                </Link>
              </div>
            </div>
          )}

          {!loading && confirmed && (
            <div className="flex flex-col items-center py-6 text-center">
              <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-3">
                <ShieldCheck className="w-7 h-7 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-base font-semibold text-[var(--theme-text-primary)]">
                Hold authorised
              </p>
              <p className="text-sm text-[var(--theme-text-muted)] mt-1">
                Returning to chat…
              </p>
            </div>
          )}

          {/* Saved-card path */}
          {!loading && !error && !confirmed && preauth && showSavedCards && (
            <SavedCardForm
              preauth={preauth}
              savedCards={savedCards}
              onSuccess={handleSuccess}
              onUseDifferent={() => setForceNewCard(true)}
            />
          )}

          {/* New-card path */}
          {!loading && !error && !confirmed && preauth && !showSavedCards && elementsOptions && (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <NewCardForm
                preauth={preauth}
                hasSavedCards={savedCards.length > 0}
                onSuccess={handleSuccess}
                onCancel={() => setForceNewCard(false)}
              />
            </Elements>
          )}
        </div>
      </div>
    </Layout>
  );
}
